import { Navigate } from "react-router-dom";
import { LoginPage } from "@/pages/Login";
import { RegisterPage } from "@/pages/Register";
import { EmailVerificationPage } from "@/pages/EmailVerification";
import { ResetPasswordRequestPage } from "@/pages/ResetPasswordRequest";
import { ResetPasswordPage } from "@/pages/ResetPassword";
import { DashboardPage } from "@/pages/Dashboard";
import Layout from "@/components/Layout";
import NotFound from "@/pages/NotFound";
import { Loader2 } from "lucide-react";
import { IS_DEMO_MODE } from "@/config/config";

interface GenerateRoutesProps {
    userProfile: unknown;
    loading: boolean;
    user: unknown;
}

const generateRoutes = ({
    userProfile: _userProfile,
    loading,
    user,
}: GenerateRoutesProps) => {
    const isAuthenticated = !!user;

    // Loading spinner for auth checks
    const LoadingSpinner = () => (
        <div className="flex items-center justify-center min-h-screen">
            <Loader2 className="h-8 w-8 animate-spin" />
        </div>
    );

    // Default page logic
    const defaultPage = <Navigate to="/dashboard" replace />;

    // Protected element wrapper
    const protectedElement = (
        component: JSX.Element,
        redirectTo: string = window.location.pathname + window.location.search
    ) => {
        // Always show loading spinner while auth is loading
        if (loading) {
            console.log("Loading spinner - auth still loading");
            return <LoadingSpinner />;
        }

        // Only check authentication after loading is complete
        const isAuthenticatedAndLoaded = !!user && !loading;

        return isAuthenticatedAndLoaded
            ? component
            : <Navigate to="/login" state={{ from: redirectTo }} replace />;
    };

    // Public element wrapper (for login/register pages)
    const publicElement = (component: JSX.Element) => {
        if (loading) {
            return <LoadingSpinner />;
        }

        // If already authenticated, redirect to dashboard
        console.log("public and isAuthenticated", isAuthenticated)
        return isAuthenticated
            ? <Navigate to="/dashboard" replace />
            : component;
    };

    return [
        {
            path: "/",
            element: <Layout />,
            children: [
                {
                    index: true,
                    element: loading ? <LoadingSpinner /> : (isAuthenticated ? defaultPage : <Navigate to="/login" replace />)
                },
                {
                    path: "login",
                    element: publicElement(<LoginPage />)
                },
                {
                    path: "register",
                    element: publicElement(<RegisterPage />)
                },
                {
                    path: "verify-email",
                    element: publicElement(<EmailVerificationPage />)
                },
                {
                    path: "reset-password-request",
                    element: publicElement(<ResetPasswordRequestPage />)
                },
                {
                    path: "reset-password",
                    element: IS_DEMO_MODE ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />
                },
                {
                    path: "dashboard",
                    element: protectedElement(<DashboardPage />)
                },
                {
                    path: "*",
                    element: <NotFound />
                },
            ],
        },
    ];
};

export default generateRoutes; 